// External Dependencies
import { useCallback, useState } from 'react';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MUISelect, { type SelectProps as MUISelectProps } from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import styled from 'styled-components';

// Local Typings
export interface SelectOption {
  id: number | string;
  label: string;
}
// error and required are throwing strange warnings if we expect a boolean instead of a function.
// The reason is unknown for now. This works as expected if passing type any.
export interface SelectProps extends Omit<MUISelectProps, 'variant'> {
  error?: any;
  errorMessage?: string | null;
  forceError?: boolean;
  fullWidth?: boolean;
  hideLabel?: boolean;
  label: string;
  name: string;
  options: SelectOption[];
  required?: any;
  testId?: string;
  value: number | string;
  variant?: 'filled' | 'outlined' | 'standard';
}

// Local Variables
// The height of a menu item
const ITEM_HEIGHT = 48;

const StyledFormControl = styled(FormControl)({
  '.select': {
    // This lets the user see a partial item and indicates scroll as an option
    maxHeight: ITEM_HEIGHT * 4.6,
  },
  '.shrink-label': {
    marginTop: 12,
  },
  minWidth: 200,
  textAlign: 'left',
});

// Component Definition
const Select = ({
  error = false,
  errorMessage = null,
  forceError = false,
  fullWidth = true,
  hideLabel = false,
  label,
  name,
  onChange,
  options,
  required = false,
  testId,
  value,
  variant = 'filled',
  ...props
}: SelectProps): JSX.Element => {
  const [isTouched, setIsTouched] = useState(false);

  const handleBlur = useCallback(() => {
    setIsTouched(true);
  }, []);

  const hasError = (error && isTouched) || forceError;

  return (
    <StyledFormControl fullWidth={fullWidth}>
      {!hideLabel && (
        <InputLabel
          className="label"
          classes={{ shrink: 'shrink-label' }}
          htmlFor={name}
        >
          {label}
        </InputLabel>
      )}
      <MUISelect
        MenuProps={{ PopoverClasses: { paper: 'select' } }}
        error={hasError}
        inputProps={{
          'data-testid': testId,
          id: name,
        }}
        name={name}
        native
        onBlur={handleBlur}
        onChange={onChange}
        type="text"
        value={value}
        variant={variant}
        {...props}
      >
        {!required && (
          <option
            aria-label="None"
            value=""
          />
        )}
        {options.length && options.map(({ id, label: optionLabel }) => (
          <option
            key={id}
            value={id}
          >
            {optionLabel}
          </option>
        ))}
      </MUISelect>

      {(hasError || forceError) && (
        <FormHelperText>
          <Typography color="error">
            {errorMessage}
          </Typography>
        </FormHelperText>
      )}
    </StyledFormControl>
  );
};

export default Select;
